import { useQueryClient } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { useEffect } from 'react'
import { useAnalytics } from '~/hooks/useAnalytics'
import emitterService from '~/services/EventEmitter/EmitterService'
import { AuthenticationEmitterEventType } from '~/services/EventEmitter/types'
import Logger from '~/services/Logger'
import {
  AnalyticsNamespace,
  AuthenticationAnalyticsEvents,
} from '~/types/Analytics'
import {
  AuthenticationState,
  AuthenticationUpdateType,
} from '~/types/AuthenticationTypes'
import { UserState } from '~/types/UserStateTypes'
import { isProductionBuildEnvironment } from '~/utils/common'
import { resetPosthog } from '~/utils/initializePosthog'
import { clearLeanPopupPreference } from '~/utils/leanPopup'

import {
  AppSessionJwts as AppSessionJwtData,
  SessionTokenData,
  ValidationTokenData,
} from '@mangovoice/types'

import { StorageKeys } from './StorageKeys'

const logger = new Logger('AuthenticationState')

/**
 * Helper function to get stack trace (excluding current frame)
 */
const getStackTrace = (): string => {
  const stack = new Error().stack
  return stack?.split('\n').slice(2, 8).join('\n') || 'unknown'
}

/**
 * Helper function to summarize state (avoid logging full state)
 */
const summarizeState = (
  state: UserState,
): {
  hasPbx: boolean
  hasSessionUser: boolean
  pbxId?: number
  userId?: number
} => {
  return {
    hasPbx: Boolean(state.pbx),
    hasSessionUser: Boolean(state.sessionUser),
    pbxId: state.pbx?.id,
    userId: state.sessionUser?.id,
  }
}

/**
 * Helper function to check if auth token exists in localStorage.
 * Used to provide context when user-session-data parse errors occur.
 */
const hasAuthTokenInLocalStorage = (): boolean => {
  try {
    const authData = localStorage.getItem('authData')
    if (!authData) return false
    const authState: AuthenticationState = JSON.parse(authData)
    return Boolean(authState.appSessionJwts?.access_token)
  } catch {
    return false
  }
}

// Track last known non-empty state for detecting unexpected clearing
let lastKnownState: UserState | null = null
let lastSetTimestamp: number | null = null
// Track when we're intentionally clearing state (e.g., during logout)
let isIntentionalClear = false

/**
 * Custom storage handler for userStateAtom that safely handles localStorage operations.
 * Provides error handling, logging, and context for recovery mechanisms.
 */
const createSafeUserStateStorage = () => {
  return {
    getItem: (key: string, initialValue: UserState): UserState => {
      try {
        const item = localStorage.getItem(key)
        if (!item) {
          return initialValue
        }

        const parsed = JSON.parse(item)
        // Track non-empty state for comparison
        if (
          parsed &&
          typeof parsed === 'object' &&
          Object.keys(parsed).length > 0
        ) {
          lastKnownState = parsed
        }
        return parsed
      } catch (error) {
        logger.error(
          'Failed to parse user-session-data from localStorage',
          error as Error | null,
          {
            key,
          },
        )
        // If JSON is corrupted, check if auth exists for recovery context
        const hasAuthToken = hasAuthTokenInLocalStorage()
        if (hasAuthToken) {
          logger.warn(
            'user-session-data parse error but auth token exists - recovery may be needed',
          )
        }
        return initialValue
      }
    },
    setItem: (key: string, value: UserState): void => {
      try {
        const isEmptyObject =
          value && typeof value === 'object' && Object.keys(value).length === 0

        // Track when state becomes empty unexpectedly (breadcrumb #4)
        if (
          isEmptyObject &&
          !isIntentionalClear &&
          lastKnownState &&
          Object.keys(lastKnownState).length > 0
        ) {
          const timeSinceLastSet = lastSetTimestamp
            ? Date.now() - lastSetTimestamp
            : null
          logger.error('user-session-data became empty unexpectedly', null, {
            previousState: summarizeState(lastKnownState),
            currentState: {},
            timeSinceLastSet,
            caller: getStackTrace(),
            timestamp: Date.now(),
          })
        }

        // Log when setting to empty object (except during intentional logout)
        if (isEmptyObject && !isIntentionalClear) {
          const logData: {
            key: string
            stackTrace?: string
            previousState?: ReturnType<typeof summarizeState>
          } = { key }
          // Only include stack trace in non-production environments
          if (!isProductionBuildEnvironment()) {
            logData.stackTrace = getStackTrace()
          }
          if (lastKnownState && Object.keys(lastKnownState).length > 0) {
            logData.previousState = summarizeState(lastKnownState)
          }
          logger.error(
            'setUserState({}) called - empty object being written to localStorage',
            null,
            logData,
          )
        } else {
          // Track successful non-empty writes
          lastKnownState = value
          lastSetTimestamp = Date.now()
        }

        localStorage.setItem(key, JSON.stringify(value))
      } catch (error) {
        logger.error(
          'Failed to write user-session-data to localStorage',
          error as Error | null,
          {
            key,
          },
        )
      }
    },
    removeItem: (key: string): void => {
      // Track localStorage.removeItem() calls on user-session-data (breadcrumb #1)
      if (key === StorageKeys.UserSessionData) {
        const previousState = lastKnownState
          ? summarizeState(lastKnownState)
          : null
        logger.warn('user-session-data removed from localStorage', {
          caller: getStackTrace(),
          previousState,
          timestamp: Date.now(),
        })
        lastKnownState = null
        lastSetTimestamp = null
      }
      localStorage.removeItem(key)
    },
    // Subscribe to storage events from other windows (needed for cross-window sync)
    subscribe: (
      key: string,
      callback: (value: UserState) => void,
      initialValue: UserState,
    ): (() => void) => {
      if (typeof window === 'undefined') {
        return () => {}
      }

      const storageEventCallback = (e: StorageEvent) => {
        if (e.key === key && e.newValue) {
          try {
            const newValue = JSON.parse(e.newValue) as UserState
            callback(newValue)
          } catch {
            callback(initialValue)
          }
        }
      }

      window.addEventListener('storage', storageEventCallback)
      return () => window.removeEventListener('storage', storageEventCallback)
    },
  }
}

export const userStateAtom = atomWithStorage<UserState>(
  StorageKeys.UserSessionData,
  {},
  createSafeUserStateStorage(),
)

export interface UseAuthenticationStateResponse {
  authenticationState: AuthenticationState
  jwtData?: AppSessionJwtData
  validationTokenData?: ValidationTokenData
  sessionData?: SessionTokenData
  isUserLoggedIn: boolean
  clearAllAuthenticationData: () => void
  logout: () => void
}

const localAuthStore = localStorage.getItem('authData')
let localAuthState: AuthenticationState & {
  _isRefreshingAuth?: boolean
  _lastRefreshAttempt?: number
  _refreshAttemptCount?: number
} = (() => {
  if (!localAuthStore) {
    return {
      _isRefreshingAuth: false,
      _lastRefreshAttempt: undefined,
      _refreshAttemptCount: 0,
    }
  }
  try {
    return {
      ...JSON.parse(localAuthStore),
      // Always reset refresh state on startup to prevent stuck flags
      _isRefreshingAuth: false,
      _lastRefreshAttempt: undefined,
      _refreshAttemptCount: 0,
    }
  } catch {
    return {
      _isRefreshingAuth: false,
      _lastRefreshAttempt: undefined,
      _refreshAttemptCount: 0,
    }
  }
})()

const broadcastLocalAuthStateUpdated = (
  updateType: AuthenticationUpdateType,
) => {
  emitterService.emit(AuthenticationEmitterEventType.USER_AUTH_DATA_UPDATED, {
    newAuthState: localAuthState,
    updateType,
  })
}

const getRefreshCooldown = (attemptCount: number): number => {
  const baseDelay = 2000
  const maxDelay = 30000
  return Math.min(baseDelay * Math.pow(2, attemptCount), maxDelay)
}

export const getCurrentAuthState = (): AuthenticationState => ({
  ...localAuthState,
})

let _setValidationTokenHook:
  ((newValidationToken?: ValidationTokenData) => void) | undefined

export const setValidationToken = (
  newValidationToken?: ValidationTokenData,
) => {
  localAuthState = {
    ...localAuthState,
    validationToken: newValidationToken,
  }
  _setValidationTokenHook?.(newValidationToken)
  broadcastLocalAuthStateUpdated(AuthenticationUpdateType.ValidationToken)
}
let _setSessionHook: ((newSession?: SessionTokenData) => void) | undefined

export const setSession = (newSession?: SessionTokenData) => {
  localAuthState = {
    ...localAuthState,
    session: newSession,
  }
  _setSessionHook?.(newSession)
  broadcastLocalAuthStateUpdated(AuthenticationUpdateType.Session)
}
let _setAppSessionJwtsHook:
  ((newAppSessionJwts?: AppSessionJwtData) => void) | undefined

export const setAppSessionJwts = (
  newAppSessionJwts?: AppSessionJwtData,
): boolean => {
  // Note: We no longer validate token expiration here.
  // Expired tokens will be automatically refreshed by the 401 interceptor on the first API call.
  // This allows users to stay logged in across sessions.

  localAuthState = {
    ...localAuthState,
    appSessionJwts: newAppSessionJwts,
    _refreshAttemptCount: 0,
  }
  _setAppSessionJwtsHook?.(newAppSessionJwts)
  broadcastLocalAuthStateUpdated(AuthenticationUpdateType.AppSessionJwt)
  return true
}

export const canAttemptRefresh = (): boolean => {
  if (localAuthState._isRefreshingAuth) return false

  if (localAuthState._lastRefreshAttempt) {
    const cooldown = getRefreshCooldown(
      localAuthState._refreshAttemptCount ?? 0,
    )
    if (Date.now() - localAuthState._lastRefreshAttempt < cooldown) {
      return false
    }
  }

  return true
}

export const setRefreshInProgress = (inProgress: boolean) => {
  localAuthState = {
    ...localAuthState,
    _isRefreshingAuth: inProgress,
    _lastRefreshAttempt: inProgress
      ? Date.now()
      : localAuthState._lastRefreshAttempt,
    _refreshAttemptCount: inProgress
      ? (localAuthState._refreshAttemptCount ?? 0) + 1
      : localAuthState._refreshAttemptCount,
  }

  // Broadcast state change so WebSocketService can react
  if (!inProgress) {
    broadcastLocalAuthStateUpdated(AuthenticationUpdateType.AppSessionJwt)
  }
}

const authenticationStateAtom = atomWithStorage<AuthenticationState>(
  StorageKeys.AuthData,
  {},
)

export const getAuthStateFromLocalStorage = (): AuthenticationState => {
  try {
    const _localAuthStore = localStorage.getItem('authData')
    const _localAuthState: AuthenticationState = _localAuthStore
      ? JSON.parse(_localAuthStore)
      : {}
    return _localAuthState
  } catch (error) {
    logger.error('Failed to parse authData from localStorage', null, { error })
    return {}
  }
}
export const useAuthenticationState = (): UseAuthenticationStateResponse => {
  const [, setUserState] = useAtom(userStateAtom)
  const [authenticationState, setAuthenticationState] = useAtom(
    authenticationStateAtom,
  )
  const queryClient = useQueryClient()
  const { track } = useAnalytics(AnalyticsNamespace.AUTHENTICATION)

  const isUserLoggedIn = Boolean(
    authenticationState.appSessionJwts?.access_token,
  )

  useEffect(() => {
    setAuthenticationState(localAuthState)
    // Note: We allow expired tokens from localStorage to be set here.
    // They will be automatically refreshed by the 401 interceptor on the first API call.
  }, [])

  _setValidationTokenHook = (newValidationToken?: ValidationTokenData) => {
    setAuthenticationState({
      ...localAuthState,
      validationToken: newValidationToken,
    })
  }

  _setAppSessionJwtsHook = (newAppSessionJwts?: AppSessionJwtData) => {
    setAuthenticationState({
      ...localAuthState,
      appSessionJwts: newAppSessionJwts,
    })
  }

  _setSessionHook = (newSessionData?: SessionTokenData) => {
    setAuthenticationState({
      ...localAuthState,
      session: newSessionData,
    })
  }

  const clearAllAuthenticationData = () => {
    localAuthState = {}
    setAuthenticationState(localAuthState)
  }

  const logout = () => {
    if (!isUserLoggedIn) {
      return
    }

    clearAllAuthenticationData()

    //allow components to respond to logout
    emitterService.emit(
      AuthenticationEmitterEventType.USER_LOGGED_OUT,
      undefined,
    )

    //track logout event
    track(AuthenticationAnalyticsEvents.LogoutSuccess)

    //clear all query caches in the query client
    queryClient.clear()

    //reset PostHog identity
    resetPosthog()

    //wipe authentication state local storage
    localStorage.removeItem('authData')
    clearLeanPopupPreference()
    logger.info('Logout: clearing user-session-data (intentional)')
    // Set flag to skip error logging for intentional logout
    isIntentionalClear = true
    setUserState({})
    // Reset flag after state is cleared
    isIntentionalClear = false

    //navigate to login page by reloading (triggers router auth check)
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }

  return {
    authenticationState,
    jwtData: authenticationState.appSessionJwts,
    validationTokenData: authenticationState.validationToken,
    sessionData: authenticationState.session,
    isUserLoggedIn,
    clearAllAuthenticationData,
    logout,
  }
}
